import { useCallback, useRef } from "react";

import { useStreamChat } from "./useStreamChat";

import type { ChatMessageContent } from "../../../services/aiChat";

interface UseInitContainerOptions {
  onComplete?: (containerId: string | null, summary: string) => void;
}

const SUMMARY_START = "<<<DATA_SUMMARY>>>";
const SUMMARY_END = "<<<END_DATA_SUMMARY>>>";

export const DEFAULT_INIT_PROMPT =
  "Load and inspect all attached files. When finished, output a concise summary " +
  "of the data file contents (date range, key metrics, data-quality notes). " +
  `Wrap ONLY the summary between the markers ${SUMMARY_START} and ${SUMMARY_END}. ` +
  "Do not place any code, narration, or follow-up questions inside the markers, " +
  "and do not ask what to do next.";

/**
 * Pulls the delimited summary out of the init transcript. The warm-up runs code
 * to inspect the files, so the raw response is full of tool calls and narration;
 * the model is instructed to fence the clean summary between markers. Falls back
 * to the trimmed raw text if the markers are missing (model ignored them).
 */
export const extractDataSummary = (raw: string): string => {
  const start = raw.indexOf(SUMMARY_START);
  if (start === -1) return raw.trim();
  const from = start + SUMMARY_START.length;
  const end = raw.indexOf(SUMMARY_END, from);
  return (end === -1 ? raw.slice(from) : raw.slice(from, end)).trim();
};

/**
 * Fires a silent container warm-up against `/ai/chat` using the file IDs
 * returned from `uploadAiData`. The response's container_id is forwarded to
 * `onComplete` so callers can replace the pre-warm container_id with the
 * chat-ready one. Errors are swallowed (logged) — the user-facing chat flow
 * still works against the original upload_data container_id if init fails.
 */
export const useInitContainer = ({
  onComplete,
}: UseInitContainerOptions = {}) => {
  // Hold the latest onComplete in a ref so the callbacks passed to
  // useStreamChat stay referentially stable. Inline closures here would give
  // streamChat (and therefore initContainer) a new identity every render,
  // defeating the useCallback below and re-rendering every consumer.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handleComplete = useCallback(
    (
      content: string,
      _stopReason: string,
      _usage: { input_tokens: number; output_tokens: number },
      containerId?: string | null,
    ) => {
      // Extract from the full transcript (not the text-only capture, which is
      // unreliable when the backend doesn't tag blocks as "text") so the
      // delimited summary survives regardless of block tagging.
      onCompleteRef.current?.(containerId ?? null, extractDataSummary(content));
    },
    [],
  );

  const handleError = useCallback((err: Error) => {
    console.warn("Reboot Live container warm-up failed:", err);
  }, []);

  const { streamChat, isStreaming, streamedContent, error } = useStreamChat({
    onComplete: handleComplete,
    onError: handleError,
  });

  const initContainer = useCallback(
    (fileIds: string[], initPrompt?: string) => {
      if (fileIds.length === 0) return;

      const promptText = initPrompt?.trim() || DEFAULT_INIT_PROMPT;
      const content: ChatMessageContent[] = fileIds.map((fileId) => ({
        type: "container_upload",
        file_id: fileId,
      }));
      content.push({ type: "text", text: promptText });

      streamChat({
        session_id: `init_container_${Date.now()}`,
        messages: [{ role: "user", content }],
        save_chat: false,
      });
    },
    [streamChat],
  );

  return { initContainer, isStreaming, streamedContent, error };
};
